import { Box } from '@mui/material';
import { CountrySelect, CountrySelectCountry } from './CountrySelect';
import { StyledTextField, TextInputProps } from './TextInput';
import React from 'react';
import { InputLabel } from './InputLabel';
import { omit } from 'lodash';
import { useTheme } from '@emotion/react';

export interface PhoneNumberInputProps
  extends Omit<TextInputProps, 'type' | 'value' | 'defaultValue' | 'onChange'> {
  value?: CountrySelectCountry['value'] & { phoneNumber?: string };
  onChange?: (value: PhoneNumberInputProps['value']) => void;
}

const BasePhoneNumberInput = ({
  variant = 'outlined',
  label,
  required,
  onChange,
  ...rest
}: PhoneNumberInputProps) => {
  const theme = useTheme();

  const [phoneNumber, setPhoneNumber] = React.useState('');
  const onNumberChange: TextInputProps['onChange'] = e => {
    e.stopPropagation();
    e.preventDefault();
    if (/^\d*$/.test(e.target.value)) {
      if (onChange) {
        onChange({ ...rest?.value, phoneNumber: e.target.value } as PhoneNumberInputProps['value']);
      } else {
        setPhoneNumber(e.target.value);
      }
    }
  };
  const onCountryChange: CountrySelectCountry['onChange'] = (inputValue, e, c) => {
    e.stopPropagation();
    e.preventDefault();
    onChange?.({ ...rest?.value, ...(inputValue ?? {}) } as PhoneNumberInputProps['value']);
  };
  return (
    <Box width="100%">
      {variant === 'outlined' && rest.labelSeparate && (
        <InputLabel
          isUnderline={rest?.isUnderline}
          labelVariant={rest?.labelVariant ?? 'labelSm'}
          labelColor={rest?.labelColor}
          labelWeight={rest?.labelWeight ?? 'regular'}
          required={required}
          error={rest?.error}
          disabled={rest.disabled}
        >
          {label}
        </InputLabel>
      )}
      <Box display="flex" gap="1rem" flexDirection="row" alignItems="flex-start">
        <CountrySelect
          valueType="countryObj"
          variant={variant === 'unstyled' ? 'standard' : variant}
          labelSeparate={rest.labelSeparate}
          showCountryCode
          required={required}
          disabled={rest?.disabled}
          id={`phoneNumber-input-country-select-${rest?.name}`}
          sx={{
            maxWidth: '118px',
            color: theme.palette.text.quaternary,
            '& .MuiInputBase-root ': {
              height: '44px',
              width: '100%',
            },
            '& .MuiSelect-select span:nth-of-type(3)': {
              display: 'none',
            },
          }}
          value={rest?.value}
          onChange={onCountryChange}
        />
        <StyledTextField
          fullWidth
          required={required}
          {...(!rest.labelSeparate ? { label } : {})}
          {...omit(rest, ['name'])}
          variant={variant === 'unstyled' ? 'standard' : variant}
          onBlur={e => {
            rest?.onBlur?.(e);
          }}
          value={rest?.value?.phoneNumber ?? phoneNumber}
          onChange={onNumberChange}
        />
      </Box>
    </Box>
  );
};

export const PhoneNumberInput = BasePhoneNumberInput;
